import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import { HELP_PORTAL_MODE } from "@shared/config/helpSupport";
import logger from "@shared/utils/logger";

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const WEBSITE_TOKEN = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN || "";
const BASE_URL = import.meta.env.VITE_CHATWOOT_BASE_URL || "";
const prefersChatwootPortal = HELP_PORTAL_MODE === "chatwoot";
const chatwootEnabledFlag = String(
  import.meta.env.VITE_CHATWOOT_ENABLED || "",
).trim().toLowerCase();
// Explicit true enables Chatwoot live chat even when HELP_PORTAL_MODE=local
// (guided AI desk stays in-app; human handoff uses the Chatwoot widget).
const chatwootExplicitlyEnabled = chatwootEnabledFlag === "true";
const chatwootNotDisabled = chatwootEnabledFlag !== "false";
const allowOnLocalhost =
  import.meta.env.VITE_CHATWOOT_ALLOW_LOCALHOST === "true";
const isLocalhostRuntime =
  typeof window !== "undefined" &&
  /(^|\.)localhost$/i.test(window.location.hostname || "");

const ENABLED =
  (prefersChatwootPortal || chatwootExplicitlyEnabled) &&
  chatwootNotDisabled &&
  !!WEBSITE_TOKEN &&
  !!BASE_URL &&
  (!isLocalhostRuntime || allowOnLocalhost);

// ---------------------------------------------------------------------------
// Global type augmentation for Chatwoot SDK
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setUser: (
        identifier: string,
        info: { name?: string; email?: string; identifier_hash?: string },
      ) => void;
      setLocale: (locale: string) => void;
      toggle: (state?: "open" | "close") => void;
      toggleBubbleVisibility: (state: "show" | "hide") => void;
      on: (event: string, callback: (...args: unknown[]) => void) => void;
    };
  }
}

// ---------------------------------------------------------------------------
// Context Types
// ---------------------------------------------------------------------------

interface ChatwootContextType {
  /** Whether the Chatwoot integration is configured (env vars present + mode=chatwoot). */
  enabled: boolean;
  /** Whether the SDK script has loaded and initialized successfully. */
  ready: boolean;
  /** Whether the user has granted consent for support cookies (GDPR). */
  consentGranted: boolean;
  /** Grant consent — triggers SDK script load. */
  grantConsent: () => void;
  /** Revoke consent — hides widget and removes stored consent. */
  revokeConsent: () => void;
  /** Whether route/auth/consent policy allows opening Chatwoot from NerveDX UI. */
  canOpen: boolean;
  /** Open the chat widget. */
  open: () => boolean;
  /** Close the chat widget. */
  close: () => void;
  /** Show the floating launcher bubble. */
  showLauncher: () => void;
  /** Hide the floating launcher bubble. */
  hideLauncher: () => void;
}

const ChatwootContext = createContext<ChatwootContextType | undefined>(
  undefined,
);

const disabledChatwootContext: ChatwootContextType = {
  enabled: false,
  ready: false,
  consentGranted: false,
  grantConsent: () => undefined,
  revokeConsent: () => undefined,
  canOpen: false,
  open: () => false,
  close: () => undefined,
  showLauncher: () => undefined,
  hideLauncher: () => undefined,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CONSENT_KEY = "chatwoot_consent";

const AUTH_BLOCKED_ROUTES = new Set([
  "/login",
  "/forgot-password",
  "/forgot-workspace",
]);

const SUPPORT_ENTRY_PREFIXES = [
  "/help",
  "/help?tab=documentation",
  "/training",
  "/admin",
  "/settings",
];

const CLINICAL_CONTEXT_PREFIXES = [
  "/eeg",
  "/studies",
  "/patients",
  "/reports",
];

function matchesRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

function canOpenChatwootOnRoute(pathname: string): boolean {
  if (AUTH_BLOCKED_ROUTES.has(pathname)) return false;
  if (CLINICAL_CONTEXT_PREFIXES.some((route) => matchesRoute(pathname, route))) {
    return false;
  }
  return SUPPORT_ENTRY_PREFIXES.some((route) => matchesRoute(pathname, route));
}

function getStoredConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
}

function setStoredConsent(granted: boolean) {
  try {
    if (granted) {
      localStorage.setItem(CONSENT_KEY, "granted");
    } else {
      localStorage.removeItem(CONSENT_KEY);
    }
  } catch {
    // localStorage may be unavailable
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const ChatwootProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [consentGranted, setConsentGranted] = useState(getStoredConsent);
  const scriptLoadedRef = useRef(false);
  const identifiedUserRef = useRef<string | null>(null);
  const routeAllowsOpen = canOpenChatwootOnRoute(location.pathname);
  const canOpen = Boolean(
    ENABLED && ready && isAuthenticated && consentGranted && routeAllowsOpen,
  );

  // Log configuration on first mount
  useEffect(() => {
    logger.debug("[Chatwoot] Provider mounted", {
      enabled: ENABLED,
      helpPortalMode: HELP_PORTAL_MODE,
      websiteTokenPresent: !!WEBSITE_TOKEN,
      baseUrl: BASE_URL || "(empty)",
      storedConsent: getStoredConsent(),
    });

    if (!ENABLED) {
      logger.debug("[Chatwoot] Widget disabled", {
        prefersChatwoot: prefersChatwootPortal,
        hasToken: !!WEBSITE_TOKEN,
        hasBaseUrl: !!BASE_URL,
        isLocalhost: isLocalhostRuntime,
        allowOnLocalhost,
      });
    }
  }, []);

  // Load the Chatwoot SDK script when consent is granted
  useEffect(() => {
    if (!ENABLED || !consentGranted || scriptLoadedRef.current) return;

    scriptLoadedRef.current = true;
    logger.debug("[Chatwoot] Loading SDK script", { baseUrl: BASE_URL });

    const script = document.createElement("script");
    script.src = `${BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      try {
        if (!window.chatwootSDK) {
          logger.warn("[Chatwoot] SDK script loaded but window.chatwootSDK not found");
          return;
        }
        window.chatwootSDK.run({
          websiteToken: WEBSITE_TOKEN,
          baseUrl: BASE_URL,
        });

        // Chatwoot emits a custom event when the widget is ready
        window.addEventListener("chatwoot:ready", () => {
          logger.info("[Chatwoot] Widget ready");
          setReady(true);
        });

        window.addEventListener("chatwoot:error", (ev) => {
          logger.warn("[Chatwoot] Widget error", ev);
        });
      } catch (err) {
        logger.error("[Chatwoot] Failed to initialize SDK", err);
      }
    };

    script.onerror = () => {
      logger.error("[Chatwoot] Failed to load SDK script", {
        src: script.src,
      });
      scriptLoadedRef.current = false;
    };

    document.head.appendChild(script);
  }, [consentGranted]);

  // Set user identity once widget is ready and user is authenticated
  useEffect(() => {
    if (!ready || !window.$chatwoot) return;

    if (isAuthenticated && user?.id) {
      const userId = String(user.id);
      if (identifiedUserRef.current === userId) return;

      try {
        const name =
          [user.first_name, user.last_name].filter(Boolean).join(" ") ||
          user.email ||
          "";
        window.$chatwoot.setUser(userId, {
          name,
          email: user.email || "",
        });
        identifiedUserRef.current = userId;
        logger.debug("[Chatwoot] User identity set", { userId });
      } catch (err) {
        logger.warn("[Chatwoot] Failed to set user identity", err);
      }
    } else {
      identifiedUserRef.current = null;
    }
  }, [ready, isAuthenticated, user]);

  // NerveDX is the single support front door. Keep Chatwoot's native launcher
  // hidden unless a governed NerveDX flow explicitly opens the widget.
  useEffect(() => {
    if (!ready || !window.$chatwoot) return;

    try {
      window.$chatwoot.toggleBubbleVisibility("hide");
      if (!isAuthenticated || !routeAllowsOpen) {
        window.$chatwoot.toggle("close");
      }
    } catch (err) {
      logger.warn("[Chatwoot] Failed to enforce widget route policy", err);
    }
  }, [ready, isAuthenticated, routeAllowsOpen, location.pathname]);

  // -----------------------------------------------------------------------
  // Consent management
  // -----------------------------------------------------------------------

  const grantConsent = useCallback(() => {
    logger.debug("[Chatwoot] Consent GRANTED by user");
    setStoredConsent(true);
    setConsentGranted(true);
  }, []);

  const revokeConsent = useCallback(() => {
    logger.debug("[Chatwoot] Consent REVOKED by user — cleaning up");
    setStoredConsent(false);
    setConsentGranted(false);
    scriptLoadedRef.current = false;
    setReady(false);
    identifiedUserRef.current = null;
    // Hide the widget if visible
    if (window.$chatwoot) {
      try {
        window.$chatwoot.toggleBubbleVisibility("hide");
        window.$chatwoot.toggle("close");
      } catch {
        // non-fatal
      }
    }
  }, []);

  // -----------------------------------------------------------------------
  // Widget controls
  // -----------------------------------------------------------------------

  const open = useCallback(() => {
    if (!canOpen) {
      logger.warn("[Chatwoot] open() blocked by support widget policy", {
        enabled: ENABLED,
        ready,
        isAuthenticated,
        consentGranted,
        routeAllowsOpen,
        pathname: location.pathname,
      });
      if (ready && window.$chatwoot) {
        try {
          window.$chatwoot.toggleBubbleVisibility("hide");
          window.$chatwoot.toggle("close");
        } catch {
          // non-fatal
        }
      }
      return false;
    }
    if (window.$chatwoot) {
      window.$chatwoot.toggleBubbleVisibility("hide");
      window.$chatwoot.toggle("open");
      return true;
    }
    logger.warn("[Chatwoot] open() called but widget not ready");
    return false;
  }, [
    canOpen,
    ready,
    isAuthenticated,
    consentGranted,
    routeAllowsOpen,
    location.pathname,
  ]);

  const close = useCallback(() => {
    if (ready && window.$chatwoot) {
      window.$chatwoot.toggle("close");
    } else {
      logger.warn("[Chatwoot] close() called but widget not ready");
    }
  }, [ready]);

  const showLauncher = useCallback(() => {
    if (ready && window.$chatwoot && canOpen) {
      window.$chatwoot.toggleBubbleVisibility("hide");
    } else {
      logger.warn("[Chatwoot] showLauncher() blocked by support widget policy");
    }
  }, [ready, canOpen]);

  const hideLauncher = useCallback(() => {
    if (ready && window.$chatwoot) {
      window.$chatwoot.toggleBubbleVisibility("hide");
    } else {
      logger.warn("[Chatwoot] hideLauncher() called but widget not ready");
    }
  }, [ready]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <ChatwootContext.Provider
      value={{
        enabled: ENABLED,
        ready,
        consentGranted,
        canOpen,
        grantConsent,
        revokeConsent,
        open,
        close,
        showLauncher,
        hideLauncher,
      }}
    >
      {children}
    </ChatwootContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useChatwoot = (): ChatwootContextType => {
  const context = useContext(ChatwootContext);
  // DeferredSupportChatProviders mounts Chatwoot after idle; return a safe
  // disabled stub until then so ContactSupportModal / header never crash.
  if (context === undefined) {
    return disabledChatwootContext;
  }
  return context;
};
